/**
 * MobileBlockRenderer Component
 * 
 * Renders block content in full-screen mobile view with reimagined UX:
 * - YouTube: Video + swipeable tabs (Transcript ↔ Summary)
 * - PDF: Page viewer with swipe navigation
 * - Quiz: One question per screen, swipe to advance
 * - SlideDeck: Stories-style full-screen slides
 * - Note: Full-screen BlockNote editor with actual content
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Check, X, FileText, MessageSquare, Loader2, RotateCcw } from 'lucide-react';
import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";
import { Block } from "@blocknote/core";
import { Viewer, Worker, SpecialZoomLevel, ScrollMode } from '@react-pdf-viewer/core';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';
import "@blocknote/core/fonts/inter.css";
import "@blocknote/mantine/style.css";
import './mobile.css';

// Block content types
interface YouTubeContent {
    videoId?: string;
    videoUrl?: string;
    analyzing?: boolean; // Loading state
    transcript?: Array<{ text: string; start: number; duration: number }>;
    analysis?: { summary?: string; keyPoints?: string[]; topics?: string[] };
}

interface PDFContent {
    storageUrl?: string;
    storagePath?: string;
    localUrl?: string;
    analysis?: { summary?: string; keyPoints?: string[]; topics?: string[] };
    pageCount?: number;
}

interface QuizContent {
    generating?: boolean; // Loading state
    prompt?: string; // Topic for generation
    generatedQuiz?: {
        type: 'mcq' | 'true_false' | 'fill_blank' | 'matching' | 'spot_mistake';
        title: string;
        items: Array<{
            // MCQ fields
            question?: string;
            options?: string[];
            correctAnswer?: number | string;
            answer?: number | string | 'True' | 'False';
            explanation?: string;
            // True/False fields
            statement?: string;
            // Fill Blank fields
            prompt?: string;
            accepted_variants?: string[];
            // Matching fields
            left?: string;
            right?: string;
            // Spot Mistake fields
            choices?: string[];
        }>;
    };
    answers?: Record<number, { selected: any; correct: boolean }>;
}

interface SlideDeckContent {
    generating?: boolean; // Loading state
    topic?: string; // Topic for generation
    generatedSlides?: Array<{
        id: string;
        title: string;
        layout: string;
        content: { heading?: string; body?: string; bullets?: string[] };
    }>;
    currentSlideIndex?: number;
}

interface NoteContent {
    markdown?: string;
    blocks?: unknown[];
}

type BlockContent = YouTubeContent | PDFContent | QuizContent | SlideDeckContent | NoteContent | unknown;

interface MobileBlockRendererProps {
    blockId: string;
    blockType: 'youtube' | 'pdf' | 'quiz' | 'slide-deck' | 'note';
    content?: BlockContent;
    onContentChange?: (content: BlockContent) => void;
}

// ===== YouTube Block - Enhanced Transcript =====

// Format seconds to MM:SS
const formatTimestamp = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

interface Paragraph {
    startTime: number;
    segments: Array<{ text: string; start: number; duration: number }>;
}

// Group segments into paragraphs ending at sentence boundaries
const groupIntoParagraphs = (segments: Array<{ text: string; start: number; duration: number }>): Paragraph[] => {
    if (segments.length === 0) return [];

    const paragraphs: Paragraph[] = [];
    let currentParagraph: Paragraph = { startTime: segments[0].start, segments: [segments[0]] };

    const endsWithSentence = (text: string) => /[.!?]$/.test(text.trim());
    const MAX_PARAGRAPH_DURATION = 12; // Max seconds per paragraph (shorter = more breaks)

    for (let i = 1; i < segments.length; i++) {
        const lastSeg = currentParagraph.segments[currentParagraph.segments.length - 1];
        const paragraphDuration = segments[i].start - currentParagraph.startTime;
        const prevEnd = lastSeg.start + lastSeg.duration;
        const gap = segments[i].start - prevEnd;

        if (endsWithSentence(lastSeg.text) && (gap > 1 || paragraphDuration > MAX_PARAGRAPH_DURATION)) {
            paragraphs.push(currentParagraph);
            currentParagraph = { startTime: segments[i].start, segments: [segments[i]] };
        } else {
            currentParagraph.segments.push(segments[i]);
        }
    }

    if (currentParagraph.segments.length > 0) {
        paragraphs.push(currentParagraph);
    }

    return paragraphs;
};

const MobileYouTubeBlock: React.FC<{ content: YouTubeContent }> = ({ content }) => {
    const [activeTab, setActiveTab] = useState<'transcript' | 'summary'>('summary');
    const [currentTime, setCurrentTime] = useState(0);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const playerRef = useRef<any>(null);
    const transcriptRef = useRef<HTMLDivElement>(null);
    const activeSegmentRef = useRef<HTMLSpanElement>(null);
    const timeIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const videoId = content.videoId || extractVideoId(content.videoUrl);
    const transcript = content.transcript || [];
    const paragraphs = useMemo(() => groupIntoParagraphs(transcript), [transcript]);

    // Compute paragraph segment offsets for global index
    const paragraphSegmentOffsets = useMemo(() => {
        let offset = 0;
        return paragraphs.map(para => {
            const startOffset = offset;
            offset += para.segments.length;
            return startOffset;
        });
    }, [paragraphs]);

    // Find active segment index
    const activeSegmentIndex = transcript.findIndex((seg, idx) => {
        const nextSeg = transcript[idx + 1];
        const segEnd = nextSeg ? nextSeg.start : seg.start + seg.duration;
        return currentTime >= seg.start && currentTime < segEnd;
    });

    // Initialize YouTube Player API
    useEffect(() => {
        if (!videoId) return;

        const initPlayer = () => {
            if (window.YT && window.YT.Player && iframeRef.current) {
                playerRef.current = new window.YT.Player(iframeRef.current, {
                    events: { onStateChange: startTimePolling }
                });
            }
        };

        if (!window.YT) {
            const tag = document.createElement('script');
            tag.src = 'https://www.youtube.com/iframe_api';
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
            (window as any).onYouTubeIframeAPIReady = initPlayer;
        } else if (window.YT.Player) {
            initPlayer();
        }

        return () => { playerRef.current = null; };
    }, [videoId]);

    // Poll for current time
    const startTimePolling = useCallback(() => {
        if (timeIntervalRef.current) return;
        timeIntervalRef.current = setInterval(() => {
            if (playerRef.current?.getCurrentTime) {
                try {
                    setCurrentTime(playerRef.current.getCurrentTime());
                } catch { /* Player not ready */ }
            }
        }, 250);
    }, []);

    useEffect(() => {
        startTimePolling();
        return () => {
            if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
        };
    }, [startTimePolling]);

    // Auto-scroll to center active segment
    useEffect(() => {
        if (activeSegmentRef.current && transcriptRef.current) {
            const container = transcriptRef.current;
            const segment = activeSegmentRef.current;
            const containerHeight = container.clientHeight;
            const segmentTop = segment.offsetTop - container.offsetTop;
            const segmentHeight = segment.offsetHeight;
            const scrollTarget = segmentTop - (containerHeight / 2) + (segmentHeight / 2);
            container.scrollTo({ top: scrollTarget, behavior: 'smooth' });
        }
    }, [activeSegmentIndex]);

    // Seek to timestamp
    const handleSeek = useCallback((time: number) => {
        if (playerRef.current?.seekTo) {
            playerRef.current.seekTo(time, true);
            setCurrentTime(time);
        }
    }, []);

    if (!videoId) {
        return (
            <div className="mobile-block-empty">
                <p>No video URL provided</p>
            </div>
        );
    }

    // Show loading state when analyzing
    const isAnalyzing = content.analyzing === true;

    return (
        <div className="mobile-youtube-block">
            {/* Video Player */}
            <div className="mobile-youtube-player">
                <iframe
                    ref={iframeRef}
                    id={`mobile-yt-player-${videoId}`}
                    src={`https://www.youtube.com/embed/${videoId}?enablejsapi=1&origin=${window.location.origin}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            </div>

            {/* Tab Switcher */}
            <div className="mobile-youtube-tabs">
                <button
                    className={`mobile-youtube-tab ${activeTab === 'summary' ? 'active' : ''}`}
                    onClick={() => setActiveTab('summary')}
                >
                    <FileText className="w-4 h-4" />
                    Summary
                </button>
                <button
                    className={`mobile-youtube-tab ${activeTab === 'transcript' ? 'active' : ''}`}
                    onClick={() => setActiveTab('transcript')}
                >
                    <MessageSquare className="w-4 h-4" />
                    Transcript
                </button>
            </div>

            {/* Tab Content */}
            <div className="mobile-youtube-content" ref={activeTab === 'transcript' ? transcriptRef : undefined}>
                {activeTab === 'summary' ? (
                    <div className="mobile-youtube-summary">
                        {isAnalyzing ? (
                            <div className="mobile-block-loading">
                                <Loader2 className="w-6 h-6 animate-spin" />
                                <span>Analyzing video...</span>
                            </div>
                        ) : content.analysis?.summary ? (
                            <>
                                <p className="mobile-youtube-summary-text">{content.analysis.summary}</p>
                                {content.analysis.keyPoints && content.analysis.keyPoints.length > 0 && (
                                    <div className="mobile-youtube-keypoints">
                                        <h4>Key Points</h4>
                                        <ul>
                                            {content.analysis.keyPoints.map((point, i) => (
                                                <li key={i}>{point}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </>
                        ) : (
                            <p className="mobile-youtube-empty">Analysis not available yet</p>
                        )}
                    </div>
                ) : (
                    <div className="mobile-youtube-transcript-enhanced">
                        {paragraphs.length > 0 ? (
                            paragraphs.map((para, paraIndex) => (
                                <div key={paraIndex} className="mobile-transcript-paragraph">
                                    {/* Paragraph timestamp */}
                                    <div
                                        className="mobile-transcript-time"
                                        onClick={() => handleSeek(para.startTime)}
                                    >
                                        {formatTimestamp(para.startTime)}
                                    </div>
                                    {/* Concatenated text with highlighted segments */}
                                    <p className="mobile-transcript-text">
                                        {para.segments.map((seg, segIdx) => {
                                            const globalIdx = paragraphSegmentOffsets[paraIndex] + segIdx;
                                            const isActive = globalIdx === activeSegmentIndex;
                                            return (
                                                <span
                                                    key={segIdx}
                                                    ref={isActive ? activeSegmentRef : null}
                                                    onClick={() => handleSeek(seg.start)}
                                                    className={`mobile-transcript-segment ${isActive ? 'active' : ''}`}
                                                >
                                                    {seg.text}{' '}
                                                </span>
                                            );
                                        })}
                                    </p>
                                </div>
                            ))
                        ) : (
                            <p className="mobile-youtube-empty">Transcript not available</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// ===== PDF Block - Fullscreen Immersive =====
const MobilePDFBlock: React.FC<{ content: PDFContent }> = ({ content }) => {
    const pageNavigationPluginInstance = pageNavigationPlugin();
    const { CurrentPageLabel, NumberOfPages } = pageNavigationPluginInstance;
    const [pdfUrl, setPdfUrl] = useState<string | null>(content.storageUrl || content.localUrl || null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Refresh signed URL if we have storagePath (URLs expire after 1 hour)
    useEffect(() => {
        const refreshSignedUrl = async () => {
            if (!content.storagePath) {
                // No storage path, use existing URL
                setPdfUrl(content.storageUrl || content.localUrl || null);
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                // Dynamic import to avoid circular deps
                const { supabase } = await import('@/integrations/supabase/client');
                const { data, error: urlError } = await supabase.storage
                    .from('original-pdfs')
                    .createSignedUrl(content.storagePath, 3600); // 1 hour

                if (urlError) {
                    console.error('Failed to refresh PDF URL:', urlError);
                    setError('Failed to load PDF');
                    // Fall back to stored URL
                    setPdfUrl(content.storageUrl || content.localUrl || null);
                } else if (data?.signedUrl) {
                    setPdfUrl(data.signedUrl);
                }
            } catch (err) {
                console.error('Error refreshing PDF URL:', err);
                setError('Failed to load PDF');
                setPdfUrl(content.storageUrl || content.localUrl || null);
            } finally {
                setIsLoading(false);
            }
        };

        refreshSignedUrl();
    }, [content.storagePath, content.storageUrl, content.localUrl]);

    if (isLoading) {
        return (
            <div className="mobile-pdf-loading">
                <Loader2 className="w-8 h-8 animate-spin" />
                <span>Loading PDF...</span>
            </div>
        );
    }

    if (error || !pdfUrl) {
        return (
            <div className="mobile-block-empty">
                <p>{error || 'No PDF uploaded'}</p>
            </div>
        );
    }

    return (
        <div className="mobile-pdf-fullscreen">
            {/* Thin floating page indicator */}
            <div className="mobile-pdf-page-indicator">
                <CurrentPageLabel>
                    {(props) => <span>{props.currentPage + 1}</span>}
                </CurrentPageLabel>
                <span>/</span>
                <NumberOfPages>
                    {(props) => <span>{props.numberOfPages}</span>}
                </NumberOfPages>
            </div>

            {/* Fullscreen PDF Viewer - Scrollable, pinch-to-zoom via CSS */}
            <div className="mobile-pdf-scroll-container">
                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                    <Viewer
                        fileUrl={pdfUrl}
                        defaultScale={SpecialZoomLevel.PageWidth}
                        scrollMode={ScrollMode.Vertical}
                        plugins={[pageNavigationPluginInstance]}
                        renderLoader={() => (
                            <div className="mobile-pdf-loading">
                                <Loader2 className="w-8 h-8 animate-spin" />
                                <span>Loading PDF...</span>
                            </div>
                        )}
                        renderError={() => (
                            <div className="mobile-pdf-error">Failed to load PDF</div>
                        )}
                    />
                </Worker>
            </div>
        </div>
    );
};

// ===== Quiz Block - Multi-Type Support with Swipe =====
const MobileQuizBlock: React.FC<{ content: QuizContent; onContentChange?: (c: QuizContent) => void }> = ({ content, onContentChange }) => {
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState<Record<number, { selected: any; correct: boolean }>>(content.answers || {});
    const [showResult, setShowResult] = useState(false);
    const [fillBlankInput, setFillBlankInput] = useState('');

    // Matching state
    const [selectedLeft, setSelectedLeft] = useState<number | null>(null);
    const [matchPairs, setMatchPairs] = useState<Record<number, number>>({});
    const [shuffledRightOrder, setShuffledRightOrder] = useState<number[]>([]);

    // Swipe gesture tracking
    const touchStartX = useRef<number>(0);
    const touchEndX = useRef<number>(0);
    const containerRef = useRef<HTMLDivElement>(null);

    const quiz = content.generatedQuiz;
    const quizType = quiz?.type || 'mcq';

    // Show loading state when generating
    if (content.generating === true) {
        return (
            <div className="mobile-block-loading">
                <Loader2 className="w-8 h-8 animate-spin" />
                <span>Generating quiz{content.prompt ? ` about "${content.prompt}"` : ''}...</span>
                <p className="mobile-block-loading-hint">This may take a moment</p>
            </div>
        );
    }

    if (!quiz || !quiz.items || quiz.items.length === 0) {
        return (
            <div className="mobile-block-empty">
                <p>Quiz not generated yet</p>
            </div>
        );
    }

    // Initialize shuffled order for matching quizzes (only once)
    useEffect(() => {
        if (quizType === 'matching' && shuffledRightOrder.length === 0 && quiz.items.length > 0) {
            // Create array [0, 1, 2, ...] and shuffle it
            const order = quiz.items.map((_, i) => i);
            for (let i = order.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [order[i], order[j]] = [order[j], order[i]];
            }
            setShuffledRightOrder(order);
        }
    }, [quizType, quiz.items.length, shuffledRightOrder.length]);

    const item = quiz.items[currentQuestion];
    const isAnswered = answers[currentQuestion] !== undefined;
    const totalQuestions = quiz.items.length;
    const correctCount = Object.values(answers).filter(a => a.correct).length;

    // Swipe handlers
    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const handleTouchMove = (e: React.TouchEvent) => {
        touchEndX.current = e.touches[0].clientX;
    };
    const handleTouchEnd = () => {
        const diff = touchStartX.current - touchEndX.current;
        if (Math.abs(diff) > 50) {
            if (diff > 0 && currentQuestion < totalQuestions - 1) {
                setCurrentQuestion(prev => prev + 1);
                setFillBlankInput('');
            } else if (diff < 0 && currentQuestion > 0) {
                setCurrentQuestion(prev => prev - 1);
                setFillBlankInput('');
            }
        }
    };

    // Generic answer handler - no auto-advance, user navigates manually
    const saveAnswer = (selected: any, correct: boolean, _autoAdvance = true) => {
        const newAnswers = { ...answers, [currentQuestion]: { selected, correct } };
        setAnswers(newAnswers);
        onContentChange?.({ ...content, answers: newAnswers });

        // Check if last question answered - show result
        if (Object.keys(newAnswers).length === totalQuestions) {
            // Small delay so user can see feedback, then can view result
        }
    };

    // Result screen
    if (showResult) {
        const percentage = Math.round((correctCount / totalQuestions) * 100);
        return (
            <div className="mobile-quiz-result">
                <div className="mobile-quiz-result-score">
                    <span className="mobile-quiz-result-number">{percentage}%</span>
                    <span className="mobile-quiz-result-text">{correctCount} of {totalQuestions} correct</span>
                </div>
                <button
                    className="mobile-quiz-retry"
                    onClick={() => {
                        setAnswers({});
                        setCurrentQuestion(0);
                        setShowResult(false);
                        setMatchPairs({});
                        setFillBlankInput('');
                    }}
                >
                    Try Again
                </button>
            </div>
        );
    }

    // Render quiz item based on type
    const renderQuizItem = () => {
        switch (quizType) {
            case 'true_false':
                return renderTrueFalse();
            case 'fill_blank':
                return renderFillBlank();
            case 'matching':
                return renderMatching();
            case 'spot_mistake':
                return renderSpotMistake();
            case 'mcq':
            default:
                return renderMCQ();
        }
    };

    // MCQ Renderer
    const renderMCQ = () => (
        <>
            <div className="mobile-quiz-question">
                <h3>{item.question}</h3>
            </div>
            <div className="mobile-quiz-options">
                {item.options?.map((option, i) => {
                    const isSelected = answers[currentQuestion]?.selected === i;
                    const isCorrectOption = i === item.correctAnswer || i === item.answer;
                    const showCorrect = isAnswered && isCorrectOption;
                    const showIncorrect = isAnswered && isSelected && !isCorrectOption;

                    return (
                        <button
                            key={i}
                            className={`mobile-quiz-option ${isSelected ? 'selected' : ''} ${showCorrect ? 'correct' : ''} ${showIncorrect ? 'incorrect' : ''}`}
                            onClick={() => !isAnswered && saveAnswer(i, i === (item.correctAnswer ?? item.answer))}
                            disabled={isAnswered}
                        >
                            <span className="mobile-quiz-option-letter">{String.fromCharCode(65 + i)}</span>
                            <span className="mobile-quiz-option-text">{option}</span>
                            {showCorrect && <Check className="w-5 h-5 text-emerald-500" />}
                            {showIncorrect && <X className="w-5 h-5 text-red-500" />}
                        </button>
                    );
                })}
            </div>
        </>
    );

    // True/False Renderer
    const renderTrueFalse = () => {
        const handleTF = (answer: 'True' | 'False') => {
            if (isAnswered) return;
            const correct = answer === item.answer;
            saveAnswer(answer, correct);
        };

        return (
            <>
                <div className="mobile-quiz-statement">
                    <p>{item.statement || item.question}</p>
                </div>
                <div className="mobile-quiz-tf-buttons">
                    <button
                        className={`mobile-quiz-tf-btn true ${answers[currentQuestion]?.selected === 'True' ? (answers[currentQuestion]?.correct ? 'correct' : 'incorrect') : ''} ${isAnswered && item.answer === 'True' ? 'correct' : ''}`}
                        onClick={() => handleTF('True')}
                        disabled={isAnswered}
                    >
                        True
                    </button>
                    <button
                        className={`mobile-quiz-tf-btn false ${answers[currentQuestion]?.selected === 'False' ? (answers[currentQuestion]?.correct ? 'correct' : 'incorrect') : ''} ${isAnswered && item.answer === 'False' ? 'correct' : ''}`}
                        onClick={() => handleTF('False')}
                        disabled={isAnswered}
                    >
                        False
                    </button>
                </div>
            </>
        );
    };

    // Fill Blank Renderer
    const renderFillBlank = () => {
        const checkFillBlank = () => {
            if (!fillBlankInput.trim()) return;
            const normalized = fillBlankInput.trim().toLowerCase();
            const correctAnswer = String(item.answer).toLowerCase();
            const isCorrect = normalized === correctAnswer ||
                item.accepted_variants?.some(v => v.toLowerCase() === normalized);
            saveAnswer(fillBlankInput, isCorrect || false);
        };

        // Split prompt at blank marker
        const parts = (item.prompt || item.question || '').split('__________');
        const displayValue = isAnswered ? answers[currentQuestion]?.selected : fillBlankInput;

        return (
            <>
                <div className="mobile-quiz-fill-prompt">
                    <p>
                        {parts[0]}
                        <span className={`mobile-quiz-fill-blank ${isAnswered ? (answers[currentQuestion]?.correct ? 'correct' : 'incorrect') : ''}`}>
                            {displayValue || '______'}
                        </span>
                        {parts[1]}
                    </p>
                </div>
                {!isAnswered && (
                    <div className="mobile-quiz-fill-input">
                        <input
                            type="text"
                            value={fillBlankInput}
                            onChange={(e) => setFillBlankInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && checkFillBlank()}
                            placeholder="Type your answer..."
                        />
                        <button onClick={checkFillBlank} disabled={!fillBlankInput.trim()}>
                            Check
                        </button>
                    </div>
                )}
                {isAnswered && !answers[currentQuestion]?.correct && (
                    <p className="mobile-quiz-fill-answer">Correct answer: <strong>{item.answer}</strong></p>
                )}
            </>
        );
    };

    // Matching Renderer (tap to select, tap to match - with shuffled right column)
    const renderMatching = () => {
        const allMatched = Object.keys(matchPairs).length === quiz.items.length;

        // Wait for shuffle to initialize
        if (shuffledRightOrder.length === 0) return null;

        const handleLeftTap = (idx: number) => {
            if (matchPairs[idx] !== undefined) return;
            setSelectedLeft(idx);
        };

        // shuffledIdx is the display position, originalIdx is the actual item index
        const handleRightTap = (shuffledIdx: number) => {
            const originalIdx = shuffledRightOrder[shuffledIdx];
            if (Object.values(matchPairs).includes(originalIdx)) return;
            if (selectedLeft !== null) {
                // Correct if left index matches the original right index
                const isCorrect = selectedLeft === originalIdx;
                const newPairs = { ...matchPairs, [selectedLeft]: originalIdx };
                setMatchPairs(newPairs);
                setSelectedLeft(null);

                // Check if all matched
                if (Object.keys(newPairs).length === quiz.items.length) {
                    const totalCorrect = Object.entries(newPairs).filter(([l, r]) => Number(l) === Number(r)).length;
                    saveAnswer(newPairs, totalCorrect === quiz.items.length, false);
                }
            }
        };

        return (
            <>
                <div className="mobile-quiz-matching-header">
                    <span>Match the pairs ({Object.keys(matchPairs).length}/{quiz.items.length})</span>
                </div>
                <div className="mobile-quiz-matching">
                    <div className="mobile-quiz-matching-col">
                        {quiz.items.map((q, i) => {
                            const matchedTo = matchPairs[i];
                            const isCorrect = matchedTo !== undefined && matchedTo === i;
                            return (
                                <button
                                    key={i}
                                    className={`mobile-quiz-match-item left ${selectedLeft === i ? 'selected' : ''} ${matchedTo !== undefined ? (isCorrect ? 'correct' : 'incorrect') : ''}`}
                                    onClick={() => handleLeftTap(i)}
                                    disabled={matchedTo !== undefined}
                                >
                                    {q.left}
                                </button>
                            );
                        })}
                    </div>
                    <div className="mobile-quiz-matching-col">
                        {shuffledRightOrder.map((originalIdx, shuffledIdx) => {
                            const q = quiz.items[originalIdx];
                            const isMatched = Object.values(matchPairs).includes(originalIdx);
                            const matchingLeftIdx = Object.entries(matchPairs).find(([_, r]) => r === originalIdx)?.[0];
                            const isCorrect = matchingLeftIdx !== undefined && Number(matchingLeftIdx) === originalIdx;
                            return (
                                <button
                                    key={shuffledIdx}
                                    className={`mobile-quiz-match-item right ${isMatched ? (isCorrect ? 'correct' : 'incorrect') : ''}`}
                                    onClick={() => handleRightTap(shuffledIdx)}
                                    disabled={isMatched}
                                >
                                    {q.right}
                                </button>
                            );
                        })}
                    </div>
                </div>
                {allMatched && (
                    <button className="mobile-quiz-retry" onClick={() => setMatchPairs({})}>
                        Reset
                    </button>
                )}
            </>
        );
    };

    // Spot Mistake Renderer
    const renderSpotMistake = () => (
        <>
            <div className="mobile-quiz-spot-prompt">
                <p className="mobile-quiz-spot-label">Find the mistake:</p>
                <p className="mobile-quiz-spot-text">{item.prompt || item.question}</p>
            </div>
            <p className="mobile-quiz-spot-instruction">Select the correct version:</p>
            <div className="mobile-quiz-options">
                {item.choices?.map((choice, i) => {
                    const isSelected = answers[currentQuestion]?.selected === choice;
                    const isCorrectChoice = choice === item.answer;
                    const showCorrect = isAnswered && isCorrectChoice;
                    const showIncorrect = isAnswered && isSelected && !isCorrectChoice;

                    return (
                        <button
                            key={i}
                            className={`mobile-quiz-option ${isSelected ? 'selected' : ''} ${showCorrect ? 'correct' : ''} ${showIncorrect ? 'incorrect' : ''}`}
                            onClick={() => !isAnswered && saveAnswer(choice, choice === item.answer)}
                            disabled={isAnswered}
                        >
                            <span className="mobile-quiz-option-text">{choice}</span>
                            {showCorrect && <Check className="w-5 h-5 text-emerald-500" />}
                            {showIncorrect && <X className="w-5 h-5 text-red-500" />}
                        </button>
                    );
                })}
            </div>
        </>
    );

    // Get header text based on quiz type
    const getHeaderText = () => {
        switch (quizType) {
            case 'true_false': return `Statement ${currentQuestion + 1} of ${totalQuestions}`;
            case 'matching': return 'Match the pairs';
            default: return `Question ${currentQuestion + 1} of ${totalQuestions}`;
        }
    };

    return (
        <div
            ref={containerRef}
            className="mobile-quiz-block"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            {/* Progress bar */}
            {quizType !== 'matching' && (
                <div className="mobile-quiz-progress">
                    <div
                        className="mobile-quiz-progress-bar"
                        style={{ width: `${((currentQuestion + 1) / totalQuestions) * 100}%` }}
                    />
                </div>
            )}

            {/* Header with reset button */}
            <div className="mobile-quiz-header">
                <span>{getHeaderText()}</span>
                {Object.keys(answers).length > 0 && (
                    <button
                        className="mobile-quiz-reset-btn"
                        onClick={() => {
                            setAnswers({});
                            setCurrentQuestion(0);
                            setShowResult(false);
                            setMatchPairs({});
                            setFillBlankInput('');
                            setShuffledRightOrder([]);
                        }}
                        title="Reset quiz"
                    >
                        <RotateCcw className="w-4 h-4" />
                    </button>
                )}
            </div>

            {/* Quiz content based on type */}
            {renderQuizItem()}

            {/* Explanation */}
            {isAnswered && item.explanation && quizType !== 'matching' && (
                <div className={`mobile-quiz-explanation ${answers[currentQuestion]?.correct ? 'correct' : 'incorrect'}`}>
                    <p>{item.explanation}</p>
                </div>
            )}

            {/* Navigation arrows and dots */}
            {quizType !== 'matching' && (
                <div className="mobile-quiz-nav">
                    <button
                        className="mobile-quiz-nav-btn"
                        onClick={() => { setCurrentQuestion(prev => prev - 1); setFillBlankInput(''); }}
                        disabled={currentQuestion === 0}
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>

                    <div className="mobile-quiz-dots">
                        {quiz.items.map((_, i) => (
                            <button
                                key={i}
                                className={`mobile-quiz-dot ${i === currentQuestion ? 'active' : ''} ${answers[i] ? 'answered' : ''}`}
                                onClick={() => { setCurrentQuestion(i); setFillBlankInput(''); }}
                            />
                        ))}
                    </div>

                    {currentQuestion < totalQuestions - 1 ? (
                        <button
                            className="mobile-quiz-nav-btn"
                            onClick={() => { setCurrentQuestion(prev => prev + 1); setFillBlankInput(''); }}
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    ) : Object.keys(answers).length === totalQuestions ? (
                        <button
                            className="mobile-quiz-nav-btn primary"
                            onClick={() => setShowResult(true)}
                        >
                            Done
                        </button>
                    ) : (
                        <button className="mobile-quiz-nav-btn" disabled>
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

// ===== Inline Markdown for Slides =====
// Using ReactMarkdown for consistency with rest of app
import ReactMarkdown from 'react-markdown';

// Lightweight inline markdown renderer for slides
const SlideMarkdown: React.FC<{ children: string }> = ({ children }) => (
    <ReactMarkdown
        components={{
            // Override to render inline without wrapping <p> tags
            p: ({ children }) => <>{children}</>,
            strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
            em: ({ children }) => <em>{children}</em>,
        }}
    >
        {children}
    </ReactMarkdown>
);

// ===== SlideDeck Block - Stories Style =====
const MobileSlideDeckBlock: React.FC<{ content: SlideDeckContent }> = ({ content }) => {
    const [currentSlide, setCurrentSlide] = useState(content.currentSlideIndex || 0);
    const containerRef = useRef<HTMLDivElement>(null);

    const slides = content.generatedSlides;

    // Show loading state when generating
    if (content.generating === true) {
        return (
            <div className="mobile-block-loading">
                <Loader2 className="w-8 h-8 animate-spin" />
                <span>Generating slides{content.topic ? ` about "${content.topic}"` : ''}...</span>
                <p className="mobile-block-loading-hint">This may take a moment</p>
            </div>
        );
    }

    if (!slides || slides.length === 0) {
        return (
            <div className="mobile-block-empty">
                <p>Slides not generated yet</p>
            </div>
        );
    }

    const slide = slides[currentSlide];
    const totalSlides = slides.length;

    const handleTap = (e: React.MouseEvent) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;

        if (x < rect.width / 3) {
            // Tap left - prev
            setCurrentSlide(prev => Math.max(0, prev - 1));
        } else if (x > (rect.width * 2) / 3) {
            // Tap right - next
            setCurrentSlide(prev => Math.min(totalSlides - 1, prev + 1));
        }
    };

    return (
        <div
            ref={containerRef}
            className="mobile-slidedeck-block"
            onClick={handleTap}
        >
            {/* Progress bars */}
            <div className="mobile-slidedeck-progress">
                {slides.map((_, i) => (
                    <div
                        key={i}
                        className={`mobile-slidedeck-progress-segment ${i <= currentSlide ? 'active' : ''}`}
                    />
                ))}
            </div>

            {/* Slide content */}
            <div className="mobile-slidedeck-slide">
                <h2 className="mobile-slidedeck-title">{slide.content.heading || slide.title}</h2>
                {slide.content.body && (
                    <p className="mobile-slidedeck-body"><SlideMarkdown>{slide.content.body}</SlideMarkdown></p>
                )}
                {slide.content.bullets && slide.content.bullets.length > 0 && (
                    <ul className="mobile-slidedeck-bullets">
                        {slide.content.bullets.map((bullet, i) => (
                            <li key={i}><SlideMarkdown>{bullet}</SlideMarkdown></li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Slide counter */}
            <div className="mobile-slidedeck-counter">
                {currentSlide + 1} / {totalSlides}
            </div>
        </div>
    );
};

// ===== Note Block - Full Screen BlockNote Editor =====
const MobileNoteBlock: React.FC<{ content: Block[] | NoteContent | null | undefined; onContentChange?: (c: NoteContent) => void }> = ({ content, onContentChange }) => {
    const lastLoadedContentRef = useRef<string | undefined>(undefined);

    // Check if content is markdown format
    const isMarkdownContent = (c: unknown): c is { markdown: string } => {
        return (
            c !== undefined &&
            c !== null &&
            typeof c === 'object' &&
            !Array.isArray(c) &&
            'markdown' in (c as Record<string, unknown>)
        );
    };

    // Check if content is Block[] array directly
    const isBlocksArray = (c: unknown): c is Block[] => {
        return Array.isArray(c) && c.length > 0 &&
            typeof c[0] === 'object' && c[0] !== null &&
            'type' in c[0];
    };

    // Get initial blocks for editor
    const getInitialBlocks = (): Block[] => {
        if (!content) {
            return [{ type: "paragraph", content: [] } as Block];
        }

        // Handle markdown content
        if (isMarkdownContent(content)) {
            return [{ type: "paragraph", content: [{ type: "text", text: "Loading...", styles: {} }] } as Block];
        }

        // Handle Block[] directly (desktop format)
        if (isBlocksArray(content)) {
            return content as unknown as Block[];
        }

        // Handle { blocks: Block[] } format
        if (typeof content === 'object' && 'blocks' in content && Array.isArray((content as NoteContent).blocks)) {
            const blocks = (content as NoteContent).blocks;
            if (blocks && blocks.length > 0) {
                return blocks as Block[];
            }
        }

        return [{ type: "paragraph", content: [] } as Block];
    };

    const editor = useCreateBlockNote({
        initialContent: getInitialBlocks(),
    });

    // Parse markdown content when provided
    useEffect(() => {
        if (!editor) return;

        if (isMarkdownContent(content)) {
            const markdown = content.markdown;

            // Only parse if markdown changed
            if (markdown !== lastLoadedContentRef.current) {
                lastLoadedContentRef.current = markdown;

                editor.tryParseMarkdownToBlocks(markdown).then(blocks => {
                    editor.replaceBlocks(editor.document, blocks);
                });
            }
        }
    }, [editor, content]);

    return (
        <div className="mobile-note-block">
            <div className="mobile-note-editor-wrapper">
                <BlockNoteView
                    editor={editor}
                    theme="light"
                    sideMenu={false}
                    onChange={() => {
                        // Save as Block[] directly, matching desktop NoteBlock format
                        onContentChange?.(editor.document as unknown as NoteContent);
                    }}
                />
            </div>
        </div>
    );
};

// ===== Main Renderer =====
const MobileBlockRenderer: React.FC<MobileBlockRendererProps> = ({
    blockId,
    blockType,
    content,
    onContentChange,
}) => {
    switch (blockType) {
        case 'youtube':
            return <MobileYouTubeBlock content={(content as YouTubeContent) || {}} />;
        case 'pdf':
            return <MobilePDFBlock content={(content as PDFContent) || {}} />;
        case 'quiz':
            return <MobileQuizBlock content={(content as QuizContent) || {}} onContentChange={onContentChange} />;
        case 'slide-deck':
            return <MobileSlideDeckBlock content={(content as SlideDeckContent) || {}} />;
        case 'note':
            return <MobileNoteBlock content={(content as NoteContent) || {}} onContentChange={onContentChange} />;
        default:
            return (
                <div className="mobile-block-empty">
                    <p>Unknown block type: {blockType}</p>
                </div>
            );
    }
};

// ===== Helpers =====
function extractVideoId(url?: string): string | null {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
}

function formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default MobileBlockRenderer;
